import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import "./CookieConsent.css";

const CONSENT_COOKIE_NAME = "legup_cookie_consent";

const CONSENT_VERSION = "1.0";

const CONSENT_DURATION_DAYS = 365;

/* =========================================================
   COOKIE HELPERS
========================================================= */

function getCookie(name) {
  const cookies = document.cookie.split(";").map((cookie) => cookie.trim());

  const prefix = `${name}=`;

  const found = cookies.find((cookie) => cookie.startsWith(prefix));

  if (!found) {
    return null;
  }

  return decodeURIComponent(found.substring(prefix.length));
}

function setCookie(name, value, days) {
  const expires = new Date();

  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

  const secure = window.location.protocol === "https:" ? "; Secure" : "";

  document.cookie = [
    `${name}=${encodeURIComponent(value)}`,
    `Expires=${expires.toUTCString()}`,
    "Path=/",
    "SameSite=Lax",
    secure,
  ].join("; ");
}

function readConsent() {
  const raw = getCookie(CONSENT_COOKIE_NAME);

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);

    if (parsed.version !== CONSENT_VERSION) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function saveConsent(consent) {
  const payload = {
    version: CONSENT_VERSION,

    necessary: true,

    analytics: Boolean(consent.analytics),

    updated_at: new Date().toISOString(),
  };

  setCookie(
    CONSENT_COOKIE_NAME,
    JSON.stringify(payload),
    CONSENT_DURATION_DAYS,
  );

  window.dispatchEvent(
    new CustomEvent("legup-cookie-consent-changed", {
      detail: payload,
    }),
  );

  return payload;
}

/* =========================================================
   PUBLIC HELPER
========================================================= */

export function hasAnalyticsConsent() {
  return Boolean(readConsent()?.analytics);
}

/* =========================================================
   COMPONENT
========================================================= */

export default function CookieConsent() {
  const { t } = useTranslation("home");

  const [visible, setVisible] = useState(false);

  const [settingsOpen, setSettingsOpen] = useState(false);

  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  useEffect(() => {
    const consent = readConsent();

    if (!consent) {
      setVisible(true);
      return;
    }

    setAnalyticsEnabled(Boolean(consent.analytics));
  }, []);

  useEffect(() => {
    const openSettings = () => {
      const consent = readConsent();

      setAnalyticsEnabled(Boolean(consent?.analytics));

      setVisible(true);

      setSettingsOpen(true);
    };

    window.addEventListener("legup-open-cookie-settings", openSettings);

    return () => {
      window.removeEventListener("legup-open-cookie-settings", openSettings);
    };
  }, []);

  const close = () => {
    setVisible(false);
    setSettingsOpen(false);
  };

  const acceptAll = () => {
    saveConsent({
      analytics: true,
    });

    close();
  };

  const rejectOptional = () => {
    saveConsent({
      analytics: false,
    });

    close();
  };

  const savePreferences = () => {
    saveConsent({
      analytics: analyticsEnabled,
    });

    close();
  };

  if (!visible) {
    return null;
  }

  return (
    <>
      <div className="legup-cookie-backdrop" aria-hidden="true" />

      <section
        className="legup-cookie-consent"
        role="dialog"
        aria-modal="true"
        aria-labelledby="legup-cookie-title"
      >
        {!settingsOpen ? (
          <>
            <div className="legup-cookie-copy">
              <span className="legup-cookie-kicker">
                {t("cookies.kicker", "YOUR PRIVACY")}
              </span>

              <h2 id="legup-cookie-title" className="legup-cookie-title">
                {t("cookies.title", "Cookies on LEG-UP")}
              </h2>

              <p className="legup-cookie-text">
                {t(
                  "cookies.description",
                  "We use essential cookies to ensure that the LEG-UP platform works securely. With your permission, we may also use analytics cookies to understand how the platform is used and improve the user experience.",
                )}
              </p>

              <p className="legup-cookie-text legup-cookie-text--secondary">
                {t(
                  "cookies.optional",
                  "Optional cookies are disabled unless you choose to accept them.",
                )}
              </p>
            </div>

            <div className="legup-cookie-actions">
              <button
                type="button"
                className="legup-cookie-btn legup-cookie-btn--secondary"
                onClick={rejectOptional}
              >
                {t("cookies.reject", "Reject optional")}
              </button>

              <button
                type="button"
                className="legup-cookie-btn legup-cookie-btn--secondary"
                onClick={() => setSettingsOpen(true)}
              >
                {t("cookies.settings", "Cookie settings")}
              </button>

              <button
                type="button"
                className="legup-cookie-btn legup-cookie-btn--primary"
                onClick={acceptAll}
              >
                {t("cookies.accept", "Accept all")}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="legup-cookie-settings-header">
              <div>
                <span className="legup-cookie-kicker">
                  {t("cookies.settingsKicker", "COOKIE SETTINGS")}
                </span>

                <h2 className="legup-cookie-title">
                  {t("cookies.preferencesTitle", "Manage your preferences")}
                </h2>
              </div>

              <button
                type="button"
                className="legup-cookie-close"
                aria-label={t("cookies.close", "Close")}
                onClick={() => setSettingsOpen(false)}
              >
                ×
              </button>
            </div>

            <div className="legup-cookie-categories">
              {/* Necessary */}

              <article className="legup-cookie-category">
                <div className="legup-cookie-category__content">
                  <div className="legup-cookie-category__header">
                    <h3>{t("cookies.necessary.title", "Necessary")}</h3>

                    <span className="legup-cookie-required">
                      {t("cookies.alwaysActive", "Always active")}
                    </span>
                  </div>

                  <p>
                    {t(
                      "cookies.necessary.description",
                      "These cookies are required for security, authentication, session management and core platform functionality. They cannot be disabled.",
                    )}
                  </p>
                </div>

                <label className="legup-cookie-switch legup-cookie-switch--disabled">
                  <input type="checkbox" checked disabled readOnly />

                  <span className="legup-cookie-slider" />
                </label>
              </article>

              {/* Analytics */}

              <article className="legup-cookie-category">
                <div className="legup-cookie-category__content">
                  <div className="legup-cookie-category__header">
                    <h3>{t("cookies.analytics.title", "Analytics")}</h3>
                  </div>

                  <p>
                    {t(
                      "cookies.analytics.description",
                      "Analytics cookies help us understand how visitors use LEG-UP so that we can improve navigation, performance and usability. These cookies are optional.",
                    )}
                  </p>
                </div>

                <label className="legup-cookie-switch">
                  <input
                    type="checkbox"
                    checked={analyticsEnabled}
                    onChange={(event) =>
                      setAnalyticsEnabled(event.target.checked)
                    }
                  />

                  <span className="legup-cookie-slider" />
                </label>
              </article>
            </div>

            <div className="legup-cookie-settings-actions">
              <button
                type="button"
                className="legup-cookie-btn legup-cookie-btn--secondary"
                onClick={rejectOptional}
              >
                {t("cookies.reject", "Reject optional")}
              </button>

              <button
                type="button"
                className="legup-cookie-btn legup-cookie-btn--primary"
                onClick={savePreferences}
              >
                {t("cookies.save", "Save preferences")}
              </button>
            </div>
          </>
        )}
      </section>
    </>
  );
}
